import { useQuery } from "@tanstack/react-query";
import { analyticsApi } from "@/lib/api/analytics";
import {
  DashboardStats,
  LicitacionesPorEstado,
  LicitacionesPorMes,
  TasaExitoHistorica,
  DeadlineItem,
  AIUsageStats,
} from "@/types/analytics";

// Query keys para React Query
const analyticsKeys = {
  all: ["analytics"] as const,
  dashboard: () => [...analyticsKeys.all, "dashboard"] as const,
  estadoChart: () => [...analyticsKeys.all, "estado"] as const,
  mesChart: (year: number) => [...analyticsKeys.all, "mes", year] as const,
  tasaExito: (months: number) => [...analyticsKeys.all, "tasa-exito", months] as const,
  deadlines: (days: number) => [...analyticsKeys.all, "deadlines", days] as const,
  aiUsage: () => [...analyticsKeys.all, "ai-usage"] as const,
};

/**
 * Hook para obtener estadísticas principales del dashboard.
 * Incluye KPIs como total de licitaciones, tasa de éxito, presupuestos, etc.
 */
export function useDashboardStats() {
  return useQuery<DashboardStats>({
    queryKey: analyticsKeys.dashboard(),
    queryFn: analyticsApi.getDashboardStats,
    staleTime: 60000, // 1 minuto
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook para obtener distribución de licitaciones por estado.
 * Usado para pie charts.
 */
export function useLicitacionesPorEstado() {
  return useQuery<LicitacionesPorEstado[]>({
    queryKey: analyticsKeys.estadoChart(),
    queryFn: analyticsApi.getLicitacionesPorEstado,
    staleTime: 60000, // 1 minuto
  });
}

/**
 * Hook para obtener licitaciones por mes del año especificado.
 * Usado para bar charts con totales, ganadas y perdidas.
 *
 * @param year - Año a consultar (default: año actual)
 */
export function useLicitacionesPorMes(year?: number) {
  const currentYear = year || new Date().getFullYear();

  return useQuery<LicitacionesPorMes[]>({
    queryKey: analyticsKeys.mesChart(currentYear),
    queryFn: () => analyticsApi.getLicitacionesPorMes(currentYear),
    staleTime: 300000, // 5 minutos (cambia menos frecuentemente)
  });
}

/**
 * Hook para obtener tasa de éxito histórica por mes.
 * Usado para line charts mostrando evolución en el tiempo.
 *
 * @param months - Número de meses hacia atrás (default: 12)
 */
export function useTasaExito(months: number = 12) {
  return useQuery<TasaExitoHistorica[]>({
    queryKey: analyticsKeys.tasaExito(months),
    queryFn: () => analyticsApi.getTasaExitoHistorica(months),
    staleTime: 300000, // 5 minutos
  });
}

/**
 * Hook para obtener próximos vencimientos de licitaciones.
 * Retorna licitaciones ordenadas por fecha de presentación.
 *
 * @param days - Días hacia adelante a considerar (default: 30)
 */
export function useUpcomingDeadlines(days: number = 30) {
  return useQuery<DeadlineItem[]>({
    queryKey: analyticsKeys.deadlines(days),
    queryFn: () => analyticsApi.getUpcomingDeadlines(days),
    staleTime: 30000, // 30 segundos (se actualiza frecuentemente)
    refetchInterval: 60000, // Refetch cada minuto
  });
}

/**
 * Hook para obtener estadísticas de uso de IA.
 * Incluye tokens, costo, latencia y estadísticas por agente.
 */
export function useAIUsageStats() {
  return useQuery<AIUsageStats>({
    queryKey: analyticsKeys.aiUsage(),
    queryFn: analyticsApi.getAIUsageStats,
    staleTime: 120000, // 2 minutos
  });
}
